/**
 * The name a loaded table gets in the warehouse (round 6, owner 2026-10-01; research N1 to N3).
 *
 *   **`<connection name>_<schema>_<table>`, folded to the destination's case: UPPER on Snowflake, lower on
 *   Databricks.**
 *
 * `connectionName` is the connection's Name, `dataflows.name` (instance 0014; `table_prefix` from 0012
 * until then): derived once from the connection's Display name when it is created
 * (`connectionNameFromDisplayName`), unique in the instance, and never changed afterwards, so
 * renaming a connection's Display name never moves a table. The rest is the entity
 * name with every run of characters outside `[A-Za-z0-9]` turned into one `_`
 * (`entityTableSegment`): `dbo.customers` on "Azure SQL test source" lands as
 * `AZURE_SQL_TEST_SOURCE_DBO_CUSTOMERS`, the Mouseflow stream `recordings` on "Mouseflow sandbox"
 * as `MOUSEFLOW_SANDBOX_RECORDINGS` (no placeholder schema).
 *
 * Why folded rather than the source's own case (N2): Snowflake stores an unquoted name in upper
 * case and matches it case-insensitively, and Unity Catalog stores every name in lower case. A
 * table stored the way an unquoted name folds is one an analyst, a BI tool or a dbt source can
 * name without quotes. The loader still double-quotes it in every statement (a reserved word
 * stays safe); quoting the folded spelling names the same table the bare word does.
 *
 * Two different entities can fold to one name (`customer address` and `customer_address`, or
 * Name `sales` + `eu.orders` against Name `sales_eu` + `orders`). That is refused when the
 * tables are selected (`DataflowService`, 409 `TABLE_NAME_TAKEN`), across every connection of the
 * instance, and a run that still holds two fails both with `table_name_conflict` before either
 * is touched. A target never holds `__`, so it can never be one of the loader's scratch tables
 * (`__load`, `__inc_<run>`, `__widen`, `__rebuild`).
 *
 * Columns are not named here: the loader's `cleanIdentifier` emits them
 * unquoted, so Snowflake already stores them in upper case (`runner/snowflake/identifiers.ts`).
 *
 * One module for the runner (the load), the server (the selection check, whose SQL mirror is
 * `server/services/tableNameCollisions.ts`, pinned to this by a real-database test), the client
 * (the connection editor's hint) and `scripts/print-table-names.ts`. Pure, no Node built-ins.
 */

export type WarehouseDestination = "snowflake" | "databricks";

/**
 * Owner update 2026-10-01 23:10: the destination's "Table name case", per instance
 * (`instance_bindings.table_name_case`, platform 0026).
 *
 * - `native` (the default): the whole name folded the way an unquoted name folds, UPPER on
 *   Snowflake and lower on Databricks, so nobody has to quote it.
 * - `source`: the entity's part keeps the source's own spelling (`AZURE_SQL_TEST_SOURCE_dbo_Customers`;
 *   the connection's Name is ours, so it stays upper case). Snowflake only, written as a quoted identifier,
 *   so every query has to quote it too. Unity Catalog stores every name in lower case, so on
 *   Databricks `source` reads as `native` (`effectiveTableNameCase`).
 * - `lower` (#2459, owner 2026-11-01): the whole name, the connection's Name included, in lower
 *   case (`claimsimages_dbo_ecsexp`), the spelling the old platform created. Snowflake only,
 *   written as a quoted identifier like `source`, so every query has to quote it too. On
 *   Databricks it reads as `native`, which is lower case already. A table's own name
 *   (`override`) is lower case under it as well. Columns keep their rule (upper case).
 *
 * Changing it renames nothing: a table already loaded keeps its name, and the new spelling
 * applies to tables created from then on. Each table's first load records the case it used
 * (instance 0013 `entity_table_case`), and every later load, the selection check and the editor
 * read the name in that case.
 */
export type TableNameCase = "native" | "source" | "lower";

/**
 * The case a destination actually applies: Databricks cannot keep the source's case, and its
 * native case is lower already, so both Snowflake-only cases read as `native` there.
 */
export function effectiveTableNameCase(
  destination: WarehouseDestination,
  tableNameCase: TableNameCase | null | undefined,
): TableNameCase {
  if (destination !== "snowflake") return "native";
  return tableNameCase === "source" || tableNameCase === "lower" ? tableNameCase : "native";
}

/** True for a case only Snowflake offers (`source`, `lower`); Databricks reads it as `native`. */
export function isSnowflakeOnlyTableNameCase(
  tableNameCase: TableNameCase | null | undefined,
): boolean {
  return tableNameCase === "source" || tableNameCase === "lower";
}

/** The longest connection Name, `dataflows.name` (varchar(40); instance 0014). */
export const CONNECTION_NAME_MAX = 40;

/**
 * The longest target name. Snowflake and Unity Catalog both allow 255; the loader's scratch
 * tables add a suffix (`__load` is 6, `__inc_<run token>` about 38), so a target stops at 200.
 */
export const TARGET_TABLE_MAX = 200;

/** A longer name keeps this many characters, then `_` and 8 hex of its SHA-256 (200 in all). */
const TARGET_TABLE_CUT = 191;
const HASH_CHARS = 8;

/** Lower-case words of `[a-z0-9]` joined by single underscores, starting with a letter. */
export const CONNECTION_NAME_PATTERN = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

/** The sentence a Name that fails `isConnectionName` is refused with (server and client). */
export const CONNECTION_NAME_RULE =
  "A name is lower-case letters, digits and single underscores, starts with a letter, and is at most 40 characters.";

const NON_ALPHANUMERIC_LOWER = /[^a-z0-9]+/g;
const NON_ALPHANUMERIC = /[^A-Za-z0-9]+/g;
const EDGE_UNDERSCORES = /(?:^_+)|(?:_+$)/g;
const TRAILING_UNDERSCORES = /_+$/;
const LEADING_LETTER = /^[a-z]/;

/**
 * Round 7 (backlog "A per-table name override", instance 0020 `entity_target_name`): a table's own
 * warehouse name is the connection Name's words rule at the target's length: lower-case letters
 * and digits in words joined by single underscores, starting with a letter, at most 200. So it
 * never holds `__` (no scratch table) and is folded like every name. The migration's CHECK is
 * the same pattern.
 */
export const TARGET_NAME_OVERRIDE_PATTERN = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

/** The sentence a table name that fails `isTargetNameOverride` is refused with. */
export const TARGET_NAME_OVERRIDE_RULE =
  "A table name is lower-case letters, digits and single underscores, starts with a letter, and is at most 200 characters.";

/** True when `value` may be stored as a table's own warehouse name. */
export function isTargetNameOverride(value: string): boolean {
  return value.length <= TARGET_TABLE_MAX && TARGET_NAME_OVERRIDE_PATTERN.test(value);
}

/** The table part of a table's own name: words of `[a-z0-9]` joined by single underscores. */
export const TARGET_TABLE_PART_PATTERN = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;

/** The sentence a table part that fails `TARGET_TABLE_PART_PATTERN` is refused with. */
export const TARGET_TABLE_PART_RULE =
  "The table part is lower-case letters, digits and single underscores, with no underscore at either end.";

/**
 * The fixed start of a table's own warehouse name, lower case: `<connection name>_<schema>_`, or
 * `<connection name>_` where the table has no schema. Only what follows it can be chosen. The
 * schema is the table's `sourceSchema` in the rule's words (`entityTableSegment`), so it is the
 * part of the default name `targetTableName` builds before the table.
 */
export function targetNamePrefix(
  connectionName: string,
  entity: { name: string; sourceSchema: string | null | undefined },
): string {
  // A table added by hand, or saved before its first validation, has no stored schema: the part
  // of its name before the first dot stands in, as a validated name ("dbo.customers") spells it.
  const dot = entity.name.indexOf(".");
  const schema = entityTableSegment(
    entity.sourceSchema ?? (dot > 0 ? entity.name.slice(0, dot) : ""),
  );
  return schema === "" ? `${connectionName}_` : `${connectionName}_${schema}_`;
}

/**
 * The reserved names: the watermark table an older build created (#2501: skyData no longer writes
 * or reads it, but a customer schema may still hold it) and the push stage (`SKYDATA_PUSH_<hash>`).
 */
const RESERVED_TARGET_NAMES: readonly string[] = ["skypoint_watermark"];
const RESERVED_TARGET_PREFIXES: readonly string[] = ["skydata_push_"];

/** True when the loader itself uses `name` (lower case), so no table may be loaded into it. */
export function isReservedTargetName(name: string): boolean {
  const lower = name.toLowerCase();
  return (
    RESERVED_TARGET_NAMES.includes(lower) ||
    RESERVED_TARGET_PREFIXES.some((prefix) => lower.startsWith(prefix))
  );
}

/** Why a typed table name is refused, or null: the server and the editor read this one rule. */
export type TargetNameProblem = "prefix" | "part" | "length" | "reserved";

/**
 * The rule for a table's own name: the whole name is this connection's prefix for the table and
 * a table part that follows the words rule, at most 200 characters, and not a name the loader
 * itself uses. `full` is the whole name as stored (lower case).
 */
export function targetNameProblem(
  full: string,
  connectionName: string,
  entity: { name: string; sourceSchema: string | null | undefined },
): TargetNameProblem | null {
  const prefix = targetNamePrefix(connectionName, entity);
  if (!full.startsWith(prefix)) return "prefix";
  if (!TARGET_TABLE_PART_PATTERN.test(full.slice(prefix.length))) return "part";
  if (full.length > TARGET_TABLE_MAX) return "length";
  if (isReservedTargetName(full)) return "reserved";
  return null;
}

/** True when `value` may be stored as a connection's Name (`dataflows.name`). */
export function isConnectionName(value: string): boolean {
  return value.length <= CONNECTION_NAME_MAX && CONNECTION_NAME_PATTERN.test(value);
}

/**
 * The Name a connection with Display name `name` gets (decision 2): lower case,
 * each run of characters outside `[a-z0-9]` becomes one `_`, no `_` at either end, `c_` in front
 * when it does not start with a letter, cut to 40 with no `_` left at the cut. A name with nothing
 * usable is `c`. The instance-lane insert trigger (`dataflow_name_for`,
 * `server/db/migrations/instance/0014_connection_names.sql`; 0012 before it) applies the same
 * rule in SQL.
 */
export function connectionNameFromDisplayName(name: string | null | undefined): string {
  let connectionName = (name ?? "")
    .toLowerCase()
    .replace(NON_ALPHANUMERIC_LOWER, "_")
    .replace(EDGE_UNDERSCORES, "");
  if (connectionName === "") return "c";
  if (!LEADING_LETTER.test(connectionName)) connectionName = `c_${connectionName}`;
  return connectionName.slice(0, CONNECTION_NAME_MAX).replace(TRAILING_UNDERSCORES, "");
}

/**
 * `connectionName` with `_<n>` on the end (`sales_2`), cut so the whole stays within 40: what a new
 * connection gets when another connection of the instance already holds its Name.
 */
export function numberedConnectionName(connectionName: string, n: number): string {
  const suffix = `_${n}`;
  return `${connectionName.slice(0, CONNECTION_NAME_MAX - suffix.length).replace(TRAILING_UNDERSCORES, "")}${suffix}`;
}

/**
 * The entity's part of the name: every run of characters outside `[A-Za-z0-9]` (an underscore
 * included) becomes one `_`, none at either edge, lower case. `[dbo].[Order Details]` is
 * `dbo_order_details`; `customer__address` is `customer_address`.
 */
export function entityTableSegment(entityName: string | null | undefined): string {
  return (entityName ?? "")
    .replace(NON_ALPHANUMERIC, "_")
    .replace(EDGE_UNDERSCORES, "")
    .toLowerCase();
}

/**
 * The table `entityName` of the connection named `connectionName` lands in, on `destination`.
 * `connectionName` is null only where no connection is in play (a unit test, the donor fixtures): the name is then
 * the entity's part alone, with the donor's `cdm` before one that does not start with a letter.
 */
export function targetTableName(input: {
  connectionName: string | null | undefined;
  entityName: string;
  destination: WarehouseDestination;
  /** The binding's table name case; absent is `native`. */
  tableNameCase?: TableNameCase | null;
  /**
   * Round 7: the table's own name (`entity_target_name`), which wins over the derived one. It is
   * folded like a Native name under `native` and `source`, so nobody has to quote it, and lower
   * case under `lower` (#2459).
   */
  override?: string | null;
}): string {
  const nameCase = effectiveTableNameCase(input.destination, input.tableNameCase);
  if (input.override) return foldForCase(input.override, input.destination, nameCase);
  if (nameCase === "source") return sourceCaseTableName(input.connectionName, input.entityName);
  const segment = entityTableSegment(input.entityName);
  let full: string;
  if (input.connectionName) {
    full = `${input.connectionName}_${segment === "" ? "cdm" : segment}`;
  } else {
    full = LEADING_LETTER.test(segment) ? segment : `cdm${segment}`;
  }
  // `lower` is the Native name before Snowflake's fold: the hash is over the same spelling.
  return nameCase === "lower"
    ? capName(full).toLowerCase()
    : foldForDestination(capName(full), input.destination);
}

/** Past 200 characters: 191, then `_` and 8 hex of the SHA-256 of the whole name as spelled. */
function capName(full: string): string {
  return full.length > TARGET_TABLE_MAX
    ? `${full.slice(0, TARGET_TABLE_CUT).replace(TRAILING_UNDERSCORES, "")}_${sha256Hex(full).slice(0, HASH_CHARS)}`
    : full;
}

/**
 * `source` case on Snowflake: the connection's Name upper case, the entity's part as the source spells it
 * (each run of characters outside `[A-Za-z0-9]` still one `_`), not folded.
 * `server/services/tableNameCollisions.ts` has the SQL twin, pinned by the parity test.
 */
function sourceCaseTableName(
  connectionName: string | null | undefined,
  entityName: string,
): string {
  const segment = (entityName ?? "").replace(NON_ALPHANUMERIC, "_").replace(EDGE_UNDERSCORES, "");
  let full: string;
  if (connectionName) {
    full = `${connectionName.toUpperCase()}_${segment === "" ? "CDM" : segment}`;
  } else {
    full = /^[A-Za-z]/.test(segment) ? segment : `CDM${segment}`;
  }
  return capName(full);
}

/** How one connection names its tables: what `targetTableName` needs besides the entity. */
export interface TableNaming {
  connectionName: string | null | undefined;
  destination: WarehouseDestination;
  tableNameCase?: TableNameCase | null;
  /**
   * #2501: the deployment policy fixes the Table name case, so no screen offers to change it and
   * a collision's sentence must not name that setting.
   */
  caseFixed?: boolean;
}

/** One ticked table that shares its warehouse table with another ticked table. */
export interface TargetNameCollision {
  otherEntityName: string;
  /** The name both would land as, spelled as the destination stores it. */
  target: string;
  /**
   * True when the two differ only by letter case, on Snowflake with `native` or `lower`: `source` would
   * keep them apart, so the sentence names that setting.
   */
  caseOnly: boolean;
}

/**
 * Owner update 2026-10-01 23:40: every entity of `entityNames` (the ticked ones) whose target
 * another of them shares, with the first one it shares it with. The connection editor marks
 * those rows before the save; the server's selection check (`tableNameCollisions.ts`) refuses
 * the same pairs across every connection of the instance.
 *
 * Review r2: `tableCases` holds the case each table's first load named it with (the entity
 * page's `tableNameCase`, instance 0013). It wins over `naming.tableNameCase`, because changing
 * the setting renames no table that exists. A pair is `caseOnly` only while one of the two has no
 * case of its own, since the setting would not move two that both have one.
 */
export function findTargetNameCollisions(
  entityNames: readonly string[],
  naming: TableNaming,
  tableCases?: ReadonlyMap<string, TableNameCase>,
  /** Round 7: each table's own name (`entity_target_name`), which wins over the derived one. */
  overrides?: ReadonlyMap<string, string>,
): Map<string, TargetNameCollision> {
  const byTarget = new Map<string, string[]>();
  for (const entityName of entityNames) {
    const target = targetTableName({
      ...naming,
      entityName,
      tableNameCase: tableCases?.get(entityName) ?? naming.tableNameCase,
      override: overrides?.get(entityName) ?? null,
    });
    const names = byTarget.get(target);
    if (names) names.push(entityName);
    else byTarget.set(target, [entityName]);
  }
  const found = new Map<string, TargetNameCollision>();
  for (const [target, names] of byTarget) {
    if (names.length < 2) continue;
    for (const [index, entityName] of names.entries()) {
      const otherEntityName = names[index === 0 ? 1 : 0]!;
      found.set(entityName, {
        otherEntityName,
        target,
        caseOnly:
          // A table with a name of its own is not a case question (round 7).
          !(overrides?.has(entityName) || overrides?.has(otherEntityName)) &&
          !(tableCases?.has(entityName) && tableCases.has(otherEntityName)) &&
          isCaseOnlyCollision(entityName, otherEntityName, naming),
      });
    }
  }
  return found;
}

/**
 * True when two colliding tables of ONE connection would land apart under `source` case:
 * Snowflake, the binding on `native`, and the two names differ only by letter case. Two
 * connections whose Names meet (`sales` + `eu.orders`, `sales_eu` + `orders`) are not a case
 * question, so the server asks this only for a pair of one connection.
 */
export function isCaseOnlyCollision(
  entityName: string,
  otherEntityName: string,
  naming: TableNaming,
): boolean {
  if (naming.destination !== "snowflake") return false;
  // #2501: Keep source case cannot be chosen where the deployment fixes the case.
  if (naming.caseFixed) return false;
  if (effectiveTableNameCase(naming.destination, naming.tableNameCase) === "source") return false;
  return (
    sourceCaseTableName(naming.connectionName, entityName) !==
    sourceCaseTableName(naming.connectionName, otherEntityName)
  );
}

/** The tail a case-only collision's sentence carries (owner 2026-10-01 23:40). */
const KEEP_SOURCE_CASE_TAIL =
  ", or set Table name case to Keep source case on the Destination page (Snowflake only)";

/**
 * The sentence two ticked tables of one connection that would share a warehouse table are named
 * with, in the picker before the save and in the server's 409 `TABLE_NAME_TAKEN`. It names no
 * noun for the two rows (PR #1276 review): a connector calls them Tables, Objects, Reports,
 * Resources or Entities, and the sentence must not use a word the grid above it does not.
 */
export function tableNameCollisionSentence(input: {
  entityName: string;
  otherEntityName: string;
  target: string;
  caseOnly: boolean;
}): string {
  return `Both ${input.entityName} and ${input.otherEntityName} would land as ${input.target}. Select one of them${input.caseOnly ? KEEP_SOURCE_CASE_TAIL : ""}.`;
}

/** A lower-case target name as `destination` stores it: upper case on Snowflake. */
export function foldForDestination(name: string, destination: WarehouseDestination): string {
  return destination === "snowflake" ? name.toUpperCase() : name.toLowerCase();
}

/**
 * #2459: a lower-case name (a table's own name, its fixed start, a typed preview) as the
 * destination stores it under `tableNameCase`: lower case under `lower` on Snowflake, else
 * `foldForDestination`. The one fold every screen and message uses for such a name, so it reads
 * as `targetTableName` lands it.
 */
export function foldForCase(
  name: string,
  destination: WarehouseDestination,
  tableNameCase: TableNameCase | null | undefined,
): string {
  return effectiveTableNameCase(destination, tableNameCase) === "lower"
    ? name.toLowerCase()
    : foldForDestination(name, destination);
}

/* ------------------------------------------------------------------------------------- *
 * SHA-256 (FIPS 180-4) over the UTF-8 bytes of a string, as lower-case hex. Here rather than
 * `node:crypto` because the client builds the editor's hint with this module too; the test
 * checks it against `node:crypto`.
 * ------------------------------------------------------------------------------------- */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function rotr(value: number, bits: number): number {
  return (value >>> bits) | (value << (32 - bits));
}

/** Lower-case hex SHA-256 of `text`'s UTF-8 bytes. */
export function sha256Hex(text: string): string {
  const bytes = new TextEncoder().encode(text);
  const bitLength = bytes.length * 8;
  const padded = new Uint8Array((((bytes.length + 9 + 63) >> 6) << 6) >>> 0);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  // The length in bits as a 64-bit big-endian integer; a string here is far below 2^32 bits.
  view.setUint32(padded.length - 8, Math.floor(bitLength / 0x1_0000_0000));
  view.setUint32(padded.length - 4, bitLength >>> 0);

  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const w = new Uint32Array(64);
  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i += 1) w[i] = view.getUint32(offset + i * 4);
    for (let i = 16; i < 64; i += 1) {
      const w15 = w[i - 15]!;
      const w2 = w[i - 2]!;
      const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3);
      const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }
    let a = h[0]!;
    let b = h[1]!;
    let c = h[2]!;
    let d = h[3]!;
    let e = h[4]!;
    let f = h[5]!;
    let g = h[6]!;
    let hh = h[7]!;
    for (let i = 0; i < 64; i += 1) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + s1 + ch + K[i]! + w[i]!) >>> 0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (s0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0]! + a) >>> 0;
    h[1] = (h[1]! + b) >>> 0;
    h[2] = (h[2]! + c) >>> 0;
    h[3] = (h[3]! + d) >>> 0;
    h[4] = (h[4]! + e) >>> 0;
    h[5] = (h[5]! + f) >>> 0;
    h[6] = (h[6]! + g) >>> 0;
    h[7] = (h[7]! + hh) >>> 0;
  }
  return Array.from(h, (word) => word.toString(16).padStart(8, "0")).join("");
}
